import React, {
  useContext, useEffect, useMemo, useRef, useState,
} from "react";

import { Button } from "antd";
import Modal from "antd/es/modal/Modal";
import { toast } from "react-toastify";

import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import PaymentPendingBanner from "../../../grace-period/components/PaymentPendingBanner";
import { useAccessStatus } from "../../../grace-period/hooks/useAccessStatus";
import SampleResultsModal from "../../../Product/Report/Components/ReportSpecs/SampleResultsModal";
import SampleFunctionContext from "../../SampleFunctionContext";
import SampleSubmissionContext from "../../SampleSubmissionContext";
import Comment from "../../SampleSubmissionModal/Comment";
import SampleSubmissionConfirmModal from "../../SampleSubmissionModal/SampleSubmissionConfirmModal";
import SubmissionReceiptModal from "../../SampleSubmissionModal/SubmissionReceiptModal/SubmissionReceiptModal";
import checkStatusEditable, { sampleHasParentSample, submissionHasRetestSample } from "../helper";
import ActivityHistoryModal from "./ActivityHistoryModal";
import SampleCard from "./SampleCard";
import SubmissionDetailsModalFooterComment from "./SubmissionDetailsModalFooterComment";
import SubmissionDetailsSamplesTable, {
  SUBMISSION_DETAILS_FETCH_DEFAULTS,
  SCROLLBAR_STYLE,
  EVENT_TYPE_TH_STYLE,
  renderWithHighlight,
  getVisibleSampleFields,
  filterSamplesBySearch,
} from "./SubmissionDetailsSamplesTable";
import { isScheduledSubmission } from "./submissionStatusUtils";
import SubmittedSubmissionRow from "./SubmittedSubmissionRow";

import { getLinkedFieldsForCategory } from "../../utils";

import {
  deleteSampleFromSubmission,
  getSubmittedSubmissionsInfo,
  editCommentToSubmission,
} from "../../../../actions/sampleSubmission";

import "./SubmissionDetailsModal.css";
import { ReactComponent as GridFillIcon } from "../../../../assets/icons/Sample Submission Redesign/grid-fill.svg";
import { ReactComponent as ListCheckIcon } from "../../../../assets/icons/Sample Submission Redesign/list-check.svg";
import { ReactComponent as LoadingDots } from "../../../../assets/images/common/LoadingDots.svg";
import { ReactComponent as QRIcon } from "../../../../assets/images/common/QRIcon.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/images/sample-submission/close.svg";
import searchIcon from "../../../../assets/images/sample-submission/searchIcon.svg";

import { designTokens } from "../../../../styles/tokens";

export default function SubmissionDetailsModal(props) {
  const {
    setShowSubmissionDetailsModal,
    sampleFields,
    sampleFieldsInfo,
    submissionClicked,
    showSubmissionDetailsModal,
    handleDeleteSubmission,
    handleEditSample,
    handleAddSamples,
    handleConfirmDeleteSubmission,
    refreshSubmissionsList,
    sampleLinkFieldsConfig,
    onEditScheduledSubmission,
    onDeleteScheduledSubmission,
    scheduledActionLoadingId,
  } = props;

  const [loadingSubmission, setLoadingSubmission] = useState(true);
  const [submission, setSubmission] = useState({});
  const [sampleToDelete, setSampleToDelete] = useState(null);
  const [submissionModified, setSubmissionModified] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [loadSampleDeleting, setLoadSampleDeleting] = useState(false);
  const [loadCommentUpdating, setLoadCommentUpdating] = useState(false);
  const [editComment, setEditComment] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [sampleCardViewData, setSampleCardViewData] = useState(null);
  const [sampleFieldsData, setSampleFieldsData] = useState(null);
  const [headerSearch, setHeaderSearch] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [showActivityHistoryModal, setShowActivityHistoryModal] = useState(false);
  const [activityHistorySubmission, setActivityHistorySubmission] = useState(null);
  const commentRef = useRef();
  const { sampleCategory } = useContext(SampleSubmissionContext);
  const { setOnAddBackButton } = useContext(SampleFunctionContext);
  const { areResultsLocked } = useAccessStatus();

  const apiGetSubmittedSubmission = async (submissionId) => {
    setLoadingSubmission(true);
    const { success, message, result } = await getSubmittedSubmissionsInfo({
      ...SUBMISSION_DETAILS_FETCH_DEFAULTS,
      submission_id: submissionId,
      sample_category: sampleCategory,
    });
    if (success) {
      setSubmission({
        ...submissionClicked,
        ...result,
        submit_id: submissionId,
        po: submissionClicked.po ?? result.po,
      });
    } else {
      toast.error(message);
    }
    setLoadingSubmission(false);
  };

  useEffect(() => {
    apiGetSubmittedSubmission(submissionClicked.submit_id);
  }, [setShowSubmissionDetailsModal, submissionClicked]); // eslint-disable-line

  useEffect(() => {
    if (!showSubmissionDetailsModal) setHeaderSearch("");
  }, [showSubmissionDetailsModal]);

  const visibleFields = useMemo(
    () => getVisibleSampleFields(sampleFields, sampleFieldsInfo),
    [sampleFields, sampleFieldsInfo],
  );

  const filteredSamples = useMemo(
    () => filterSamplesBySearch({
      samples: submission.samples_list,
      query: headerSearch,
      visibleFields,
    }),
    [submission.samples_list, headerSearch, visibleFields],
  );

  const highlightWords = useMemo(
    () => headerSearch.trim().split(/\s+/).filter(Boolean),
    [headerSearch],
  );

  const closeSubmissionDetailsModal = () => {
    if (submissionModified) refreshSubmissionsList();
    setShowSubmissionDetailsModal(false);
  };

  const handleOpenActivityHistory = (submissionRow) => {
    setActivityHistorySubmission(submissionRow);
    setShowActivityHistoryModal(true);
  };

  const handleConfirmDeleteSample = async (sample) => {
    setLoadSampleDeleting(true);
    if (submission.samples_list.length === 1) {
      const ok = await handleConfirmDeleteSubmission(submissionClicked, true, submissionClicked.sample_category);
      if (!ok) setLoadSampleDeleting(false);
      return;
    }
    const { success, message } = await deleteSampleFromSubmission(
      submission.submit_id,
      submission.status,
      sample,
      submissionClicked.sample_category,
    );
    if (success) {
      toast.success("Successfully deleted sample");
      setSubmissionModified(true);
      setSampleToDelete(null);
      apiGetSubmittedSubmission(submissionClicked.submit_id);
    } else {
      toast.error(message);
    }
    setLoadSampleDeleting(false);
  };

  const handleSubmitComment = async () => {
    setLoadCommentUpdating(true);
    const { success, message } = await editCommentToSubmission({
      submission_id: submission.submit_id,
      submission_status: submission.status,
      comment: commentRef.current.resizableTextArea.textArea.value.trim(),
      sample_category: sampleCategory,
    });
    if (success) {
      toast.success("Successfully updated comment");
      setSubmissionModified(true);
      setEditComment(false);
      apiGetSubmittedSubmission(submissionClicked.submit_id);
    } else {
      toast.error(message);
    }
    setLoadCommentUpdating(false);
  };

  const refreshDataAndCheckStatus = async (sampleID, sampleCategoryValue) => {
    const { success, result } = await getSubmittedSubmissionsInfo({
      ...SUBMISSION_DETAILS_FETCH_DEFAULTS,
      submission_id: submissionClicked.submit_id,
      sample_category: sampleCategory,
    });
    if (!success) return true;

    let sampleSpecFlag;
    if (sampleID) {
      const found = result.samples_list.find(
        (s) => s.sample_id === sampleID && s.sample_category === sampleCategoryValue,
      );
      if (!found) {
        toast.error("This sample was deleted");
        setSubmission({ ...result, submit_id: submissionClicked.submit_id, po: submissionClicked.po });
        return false;
      }
      sampleSpecFlag = found.specs_flag;
    }
    const isEditable = checkStatusEditable(result.status, sampleSpecFlag);
    if (!isEditable) {
      toast.error(`The submission ${sampleID ? "and/or sample " : ""}status does not allow for edits`);
      setSubmission({ ...result, submit_id: submissionClicked.submit_id, po: submissionClicked.po });
    }
    return isEditable;
  };

  const handleAddSamplesWithCheck = async () => {
    if (submissionHasRetestSample(submission)) {
      return;
    }
    if (await refreshDataAndCheckStatus()) handleAddSamples(submission);
  };

  const handleEditSampleWithCheck = async (sample) => {
    if (sampleHasParentSample(sample)) {
      return;
    }
    if (await refreshDataAndCheckStatus(sample.sample_id, sample.sample_category)) {
      handleEditSample(submission, sample);
    }
  };

  const handleViewReportData = (sample) => {
    if (!sample) return;
    const linkedFields = getLinkedFieldsForCategory(sample.sample_category, sampleLinkFieldsConfig);
    setSampleFieldsData(
      linkedFields
        .filter((key) => sampleFieldsInfo[key])
        .map((key) => ({ key: sampleFieldsInfo[key].title_field, value: sample[key] })),
    );
    setSampleCardViewData(sample);
    setShowReport(true);
  };

  const handleReceipt = () => {
    setShowReceipt((prev) => !prev);
    setOnAddBackButton(false);
  };

  const disableActions = !checkStatusEditable(submission.status, null, submission.event_id);
  const showScheduled = isScheduledSubmission({ ...submissionClicked, ...submission });
  const showType = submission.status === "-1" && submission.event_id;
  const hasNoSearchMatch = submission.samples_list?.length > 0
    && !filteredSamples.length
    && headerSearch.trim().length > 0;

  return (
    <>
      {showReceipt && (
        <SubmissionReceiptModal
          handleReceipt={handleReceipt}
          showReceipt={showReceipt}
          submissionFromParent={submission}
        />
      )}
      {showReport && (
        <SampleResultsModal
          reportData={sampleCardViewData}
          onCancel={() => setShowReport(false)}
          sampleSubmission
          sampleCategory={sampleCardViewData.sample_category}
          sampleFieldsData={sampleFieldsData}
          lockResults={areResultsLocked}
        />
      )}
      <Modal
        title=""
        open={showSubmissionDetailsModal}
        closeIcon={null}
        onCancel={closeSubmissionDetailsModal}
        centered
        destroyOnClose
        footer={null}
        wrapClassName="SubmissionDetailsModal"
      >
        <div className="submission-details-modal-container">
          <div className="SubmissionDetailsModal__Header">
            <div className="SubmissionDetailsModal__Header_Title">
              <span className="SubmissionDetailsModal__Header_TitleAccent" />
              <span className="SubmissionDetailsModal__Header_TitleText" title={submission.submit_name}>
                {renderWithHighlight(submission.submit_name, highlightWords)}
              </span>
            </div>
            <div className="SubmissionDetailsModal__Header_Actions">
              <div className="SubmissionDetailsModal__Header_Search">
                <img src={searchIcon} alt="" className="SubmissionDetailsModal__Header_SearchIcon" />
                <input
                  type="text"
                  placeholder="Search anything..."
                  value={headerSearch}
                  onChange={(e) => setHeaderSearch(e.target.value)}
                  className="SubmissionDetailsModal__Header_SearchInput"
                />
              </div>
              <div
                className="SubmissionDetailsModal__Header_ViewToggle"
                role="group"
                aria-label="Samples layout"
              >
                <button
                  type="button"
                  aria-label="Grid view"
                  aria-pressed={viewMode === "grid"}
                  className={`SubmissionDetailsModal__Header_ViewButton${viewMode === "grid" ? " SubmissionDetailsModal__Header_ViewButton--active" : ""}`}
                  onClick={() => setViewMode("grid")}
                >
                  <GridFillIcon className="SubmissionDetailsModal__Header_ViewIcon" aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label="List view"
                  aria-pressed={viewMode === "list"}
                  className={`SubmissionDetailsModal__Header_ViewButton${viewMode === "list" ? " SubmissionDetailsModal__Header_ViewButton--active" : ""}`}
                  onClick={() => setViewMode("list")}
                >
                  <ListCheckIcon className="SubmissionDetailsModal__Header_ViewIcon" aria-hidden />
                </button>
              </div>
              <button
                type="button"
                aria-label="Close"
                className="SubmissionDetailsModal__Header_Close"
                onClick={closeSubmissionDetailsModal}
              >
                <CloseIcon />
              </button>
            </div>
          </div>
          <PaymentPendingBanner />
          {sampleToDelete !== null && (
            <SampleSubmissionConfirmModal
              headerText="Delete Sample Confirmation"
              bodyText={`Are you sure you want to delete this sample?${submission.samples_list.length === 1 ? " Deleting this sample will also delete the submission." : ""} This cannot be undone.`}
              buttonText={["Cancel", "Delete", "Deleting"]}
              buttonFunctions={[
                () => setSampleToDelete(null),
                () => handleConfirmDeleteSample(submission.samples_list[sampleToDelete]),
              ]}
              loading={loadSampleDeleting}
            />
          )}
          <div className="SubmissionDetailsModal__SubmissionTableContainer">
            <table className="submission-details-modal-submission-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Submission Name</th>
                  <th>PO Number</th>
                  <th className="submitted-submissions-table-submitted-on-th">Last Action On</th>
                  <th className="submitted-submissions-table-submitted-on-th">Last Action By</th>
                  {/* <th>Submitted on</th>
                  <th>Submitted by</th> */}
                  <th className="submitted-submissions-table-samples-number-th">Samples</th>
                  <th className="submitted-submissions-table-samples-number-th">Total Tests</th>
                  {/* {sampleCategory === "product" && <th>Product Spec</th>} */}
                  {/* {sampleCategory === "environment" && <th>Environment Spec</th>} */}
                  {!disableActions && (<th className="submitted-submissions-table-samples-number-th">Actions</th>)}
                  {showScheduled && (<th className="submitted-submissions-table-samples-number-th">Actions</th>)}
                  {showType && (<th style={EVENT_TYPE_TH_STYLE}>Type</th>)}
                </tr>
              </thead>
              <tbody>
                <SubmittedSubmissionRow
                  submission={submission}
                  handleDeleteSubmission={handleDeleteSubmission}
                  handleAddSamples={handleAddSamplesWithCheck}
                  hideActionsWhenDisabled={disableActions}
                  hideActions={showScheduled}
                  showActionsColumn={showScheduled}
                  showType={showType}
                  showExtendedColumns
                  onOpenActivityHistory={handleOpenActivityHistory}
                  submissionDetailsSearchWords={highlightWords}
                  onEditScheduled={onEditScheduledSubmission}
                  onDeleteScheduled={onDeleteScheduledSubmission}
                  scheduledActionsLoading={scheduledActionLoadingId === submission.submit_id}
                />
              </tbody>
            </table>
          </div>
          <div className="SubmissionDetailsModal__Body">
            {loadingSubmission ? (
              <div className="SubmissionDetailsModal__Loading">
                <LoadingDots width={30} height={30} fill={designTokens.colorBorderPrimary} />
              </div>
            ) : (
              <StyledScrollbar
                style={SCROLLBAR_STYLE}
                noScrollX
                disableTracksWidthCompensation
              >
                {viewMode === "list" ? (
                  <div className="submission-details-modal-sample-list">
                    <SubmissionDetailsSamplesTable
                      samples={submission.samples_list}
                      filteredSamples={filteredSamples}
                      visibleFields={visibleFields}
                      sampleFieldsInfo={sampleFieldsInfo}
                      highlightWords={highlightWords}
                      submissionSampleCategory={submission.sample_category}
                      onSampleClick={handleViewReportData}
                    />
                  </div>
                ) : (
                  <div className="submission-details-modal-sample-cards">
                    {hasNoSearchMatch ? (
                      <div className="SubmissionDetailsSamplesList SubmissionDetailsSamplesList--empty SubmissionDetailsModal__SearchEmpty">
                        No samples match your search.
                      </div>
                    ) : filteredSamples.map((sample) => {
                      const idx = submission.samples_list.findIndex(
                        (s) => s.sample_id === sample.sample_id
                          && s.sample_category === sample.sample_category,
                      );
                      return (
                        <SampleCard
                          sample={sample}
                          key={`${sample.sample_id}-${sample.sample_category}-${idx}`}
                          sampleFields={sampleFields}
                          sampleFieldsInfo={sampleFieldsInfo}
                          sampleCategory={sample.sample_category}
                          submissionSampleCategory={submission.sample_category}
                          submissionStatus={submission.status}
                          handleEditSample={() => handleEditSampleWithCheck(sample)}
                          handleDeleteSample={() => setSampleToDelete(idx)}
                          handleViewReportData={() => handleViewReportData(sample)}
                          submissionEventId={submission.event_id}
                          highlightSearchWords={highlightWords}
                        />
                      );
                    })}
                  </div>
                )}
              </StyledScrollbar>
            )}
          </div>
          {!loadingSubmission && (
            <div className="SubmissionDetailsModal__Footer">
              {editComment && (
                <Comment
                  showButtons
                  commentRef={commentRef}
                  defaultValue={submission.comment}
                  placeholder="Enter your comments here"
                  handleCancelClick={() => setEditComment(false)}
                  handleSubmitClick={handleSubmitComment}
                  loading={loadCommentUpdating}
                />
              )}
              <div className="SubmissionDetailsModal__FooterButtonPanel">
                <div className="SubmissionDetailsModal__FooterCommentButton">
                  {!editComment && (
                    <SubmissionDetailsModalFooterComment
                      comment={submission.comment}
                      allowEdit={checkStatusEditable(submission.status, null, submission.event_id)}
                      handleEditComment={() => setEditComment(true)}
                    />
                  )}
                </div>
                {!showScheduled && (
                  <Button
                    type="primary"
                    onClick={handleReceipt}
                    icon={<QRIcon className="SubmissionDetailsModal__ReceiptButton__QRIcon" />}
                  >
                    Print Receipt
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>
      {showActivityHistoryModal && (
        <ActivityHistoryModal
          open={showActivityHistoryModal}
          submission={activityHistorySubmission || submission}
          onClose={() => {
            setShowActivityHistoryModal(false);
            setActivityHistorySubmission(null);
          }}
        />
      )}
    </>
  );
}
